import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Skeleton, Typography } from '@cobliteam/ui/components';
import Card, { CardContent } from '@cobliteam/ui/components/Card';
import Popover, { PopoverContent, PopoverIcon, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Icon from '@cobliteam/ui/icons';
import { colors } from '@cobliteam/ui/theme';
import { useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { z } from 'zod';
import { Shimmer } from '../elements/Shimmer';

export const chartTypeEnum = z.enum(['line', 'bar', 'area', 'pie', 'composed', 'scatter', 'radar']);

interface ChartData {
  name: string;
  value: number;
  [key: string]: string | number; // Allow additional properties for radar charts
}

interface ChartConfig {
  xAxisKey: string;
  yAxisKeys: string[];
  colors: string[];
  title?: string;
  legendNames?: string[]; // Names for legend items
}

interface LegendItem {
  name: string;
  color: string;
}

interface GraphDataOutput {
  chartType: z.infer<typeof chartTypeEnum>;
  data: ChartData[];
  config: ChartConfig;
  explanation?: string;
  title?: string; // Title at output level
  legend?: LegendItem[]; // Legend items with names and colors
}

export const ChartsDataToolSkeleton = () => {
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    setIsRendered(true);
  }, []);

  if (isRendered) {
    return null;
  }

  return (
    <Card className='my-4'>
      <CardContent>
        <div className='h-auto w-full p-4'>
          <div className='flex h-full flex-col justify-between'>
            {/* Title skeleton */}
            <div className='mb-4'>
              <Shimmer className='text-sm'>Carregando gráficos...</Shimmer>
            </div>

            {/* Chart area skeleton */}
            <div className='flex flex-1 gap-4'>
              <div className='flex-1'>
                <div className='relative h-full w-full'>
                  {/* Grid lines skeleton */}
                  <div className='absolute inset-0 flex flex-col justify-between'>
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className='h-px w-full bg-gray-3' />
                    ))}
                  </div>
                  <div className='absolute inset-0 flex items-end justify-between px-4 pb-2'>
                    {/* Chart bars/lines skeleton */}
                    {[45, 65, 35, 80, 50, 70].map((height, i) => (
                      <Skeleton key={i} className='w-8' style={{ height: `${height}%` }} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Legend skeleton */}
              <div className='flex flex-col gap-3 pt-10 pl-4'>
                {[...Array(3)].map((_, i) => (
                  <div key={i} className='flex items-center gap-2'>
                    <Skeleton className='h-3 w-3 rounded' />
                    <Skeleton className='h-4 w-20' />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ChartsDataTool = ({ part }: { part: any }) => {
  const output: GraphDataOutput = part.output;

  if (!output) return null;

  const { data, config, chartType, title: outputTitle, legend, explanation } = output;

  // Use title from output level, fallback to config.title
  const chartTitle = outputTitle || config?.title || '';

  if (!data || data.length === 0) return null;

  // Map data to recharts format
  // For all charts, map all yAxisKeys from the item
  const chartData = data.map((item) => {
    const mapped: Record<string, string | number> = {
      [config.xAxisKey]: item[config.xAxisKey] ?? item.name,
    };
    // Add all yAxisKeys from the item
    for (const key of config.yAxisKeys) {
      mapped[key] = item[key] ?? item.value ?? 0;
    }
    return mapped;
  });

  // Calculate max value for domain - consider all yAxisKeys
  const allValues: number[] = [];
  for (const key of config.yAxisKeys) {
    for (const item of data) {
      const value = item[key] ?? item.value;
      if (typeof value === 'number') {
        allValues.push(value);
      }
    }
  }
  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
  const domainMax = Math.ceil(maxValue * 1.2); // Add 20% padding

  // Custom legend component for left side
  const renderCustomLegend = (iconType: 'line' | 'square' = 'square') => {
    if (config.yAxisKeys.length === 0) return null;

    return (
      <div className='flex flex-col gap-3 pt-4 pl-4'>
        <div className='flex self-end'>
          {explanation && (
            <Popover>
              <PopoverTrigger>
                <div>
                  <PopoverIcon />
                </div>
              </PopoverTrigger>
              <PopoverContent className='w-48' showArrow={false}>
                <Typography variant='text-sm' color='secondary'>
                  {explanation}
                </Typography>
              </PopoverContent>
            </Popover>
          )}
        </div>
        {config.yAxisKeys.map((key, index) => {
          // Use legend from output if available, otherwise fall back to config
          const legendItem = legend?.[index];
          const legendName = legendItem?.name || config.legendNames?.[index] || key;
          const color = legendItem?.color || config.colors[index] || colors.primary[9];

          return (
            <div key={key} className='flex items-center gap-2'>
              {iconType === 'line' ? <div className='h-0.5 w-6' style={{ backgroundColor: color }} /> : <div className='h-3 w-3 rounded' style={{ backgroundColor: color }} />}
              <Typography variant='text-sm' color='secondary'>
                {legendName === 'value' ? 'Valor' : legendName}
              </Typography>
            </div>
          );
        })}
      </div>
    );
  };

  const renderLineChart = () => (
    <div className='flex h-full flex-col justify-between'>
      <div className='flex flex-1 gap-4'>
        <div className='flex-1'>
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid stroke={colors.gray.light} strokeDasharray='3 3' />
              <XAxis dataKey={config.xAxisKey} tick={{ fill: colors.gray[11], fontSize: 12 }} axisLine={{ stroke: colors.gray[6] }} />
              <YAxis tick={{ fill: colors.gray[11], fontSize: 12 }} axisLine={{ stroke: colors.gray[6] }} domain={[0, domainMax]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.white.DEFAULT,
                  border: `1px solid ${colors.gray[6]}`,
                  borderRadius: '6px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
                labelStyle={{ color: colors.gray[12], fontWeight: 500 }}
              />
              {config.yAxisKeys.map((key, index) => {
                // Use legend from output if available, otherwise fall back to config
                const legendItem = legend?.[index];
                const legendName = legendItem?.name || config.legendNames?.[index] || key;
                const color = legendItem?.color || config.colors[index] || colors.primary[9];

                const translatedName = legendName === 'value' ? 'Valor' : legendName || key;

                return (
                  <Line
                    key={key}
                    type='monotone'
                    dataKey={key}
                    name={translatedName}
                    stroke={color}
                    strokeWidth={2}
                    dot={{ fill: color, r: 4 }}
                    activeDot={{ r: 6, stroke: color }}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {renderCustomLegend('line')}
      </div>
    </div>
  );

  const renderBarChart = () => (
    <div className='flex h-full flex-col justify-between'>
      <div className='flex flex-1 gap-4'>
        <div className='flex-1'>
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid stroke={colors.gray.light} strokeDasharray='3 3' />
              <XAxis dataKey={config.xAxisKey} tick={{ fill: colors.gray[11], fontSize: 12 }} axisLine={{ stroke: colors.gray[6] }} />
              <YAxis tick={{ fill: colors.gray[11], fontSize: 12 }} axisLine={{ stroke: colors.gray[6] }} domain={[0, domainMax]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.white.DEFAULT,
                  border: `1px solid ${colors.gray[6]}`,
                  borderRadius: '6px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
                labelStyle={{ color: colors.gray[12], fontWeight: 500 }}
              />
              {config.yAxisKeys.map((key, index) => {
                const translatedName = config.legendNames?.[index]?.[0] === 'value' ? 'Valor' : config.legendNames?.[index] || key;
                return (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={translatedName === 'value' ? 'Valor' : translatedName}
                    fill={config.colors[index] || colors.primary[9]}
                    radius={[4, 4, 0, 0]}
                  />
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        </div>
        {renderCustomLegend('square')}
      </div>
    </div>
  );

  const renderAreaChart = () => (
    <div className='flex h-full flex-col justify-between'>
      {chartTitle && (
        <div className='mb-4'>
          <Typography variant='heading-lg'>{chartTitle}</Typography>
        </div>
      )}
      <div className='flex flex-1 gap-4'>
        <div className='flex-1'>
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid stroke={colors.gray.light} strokeDasharray='3 3' />
              <XAxis dataKey={config.xAxisKey} tick={{ fill: colors.gray[11], fontSize: 12 }} axisLine={{ stroke: colors.gray[6] }} />
              <YAxis tick={{ fill: colors.gray[11], fontSize: 12 }} axisLine={{ stroke: colors.gray[6] }} domain={[0, domainMax]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.white.DEFAULT,
                  border: `1px solid ${colors.gray[6]}`,
                  borderRadius: '6px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
                labelStyle={{ color: colors.gray[12], fontWeight: 500 }}
              />
              {config.yAxisKeys.map((key, index) => (
                <Area
                  key={key}
                  type='monotone'
                  dataKey={key}
                  name={config.legendNames?.[index] || key}
                  stroke={config.colors[index] || colors.primary[9]}
                  fill={config.colors[index] || colors.primary[9]}
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {renderCustomLegend('line')}
      </div>
    </div>
  );

  const renderPieChart = () => {
    const accessoryColors = [
      colors.accessory.pink[9],
      colors.accessory.sky[9],
      colors.accessory.purple[9],
      colors.accessory.lime[9],
      colors.accessory.tomato[9],
      colors.accessory.orange[9],
      colors.accessory.blue[9],
      colors.accessory.gold[9],
      colors.accessory.indigo[9],
      colors.accessory.green[9],
      colors.accessory.brown[9],
      colors.accessory.teal[9],
    ];

    const pieData = data.map((item, index) => ({
      name: config.legendNames?.[index] || item.name,
      value: item.value,
      fill: config.colors[index] || accessoryColors[index % accessoryColors.length] || colors.primary[9],
    }));

    return (
      <div className='flex h-full flex-col justify-between'>
        <div className='flex flex-1 gap-4'>
          <div className='flex-1'>
            <ResponsiveContainer width='100%' height='100%'>
              <PieChart>
                <Pie
                  data={pieData}
                  cx='50%'
                  cy='50%'
                  outerRadius={80}
                  dataKey='value'
                  label={({ name, percent }) => `${name} ${percent ? (percent * 100).toFixed(0) : 0}%`}
                  labelLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: colors.white.DEFAULT,
                    border: `1px solid ${colors.gray[6]}`,
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className='flex flex-col gap-3 pt-4 pl-4'>
            <div className='flex self-end'>
              <Popover>
                <PopoverTrigger>
                  <PopoverIcon />
                </PopoverTrigger>
                <PopoverContent className='w-48' showArrow={false}>
                  <Typography variant='text-sm' color='secondary'>
                    {explanation}
                  </Typography>
                </PopoverContent>
              </Popover>
            </div>
            {pieData.map((item, index) => (
              <div key={index} className='flex items-center gap-2'>
                <div className='h-3 w-3 rounded' style={{ backgroundColor: item.fill }} />
                <Typography variant='text-sm' color='secondary'>
                  {item.name}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderScatterChart = () => (
    <div className='flex h-full flex-col justify-between'>
      {chartTitle && (
        <div className='mb-4'>
          <Typography variant='heading-lg'>{chartTitle}</Typography>
        </div>
      )}
      <div className='flex flex-1 gap-4'>
        <div className='flex-1'>
          <ResponsiveContainer width='100%' height='100%'>
            <ScatterChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid stroke={colors.gray.light} strokeDasharray='3 3' />
              <XAxis type='number' dataKey={config.yAxisKeys[0]} name={config.yAxisKeys[0]} tick={{ fill: colors.gray[11], fontSize: 12 }} axisLine={{ stroke: colors.gray[6] }} />
              <YAxis dataKey={config.xAxisKey} name={config.xAxisKey} tick={{ fill: colors.gray[11], fontSize: 12 }} axisLine={{ stroke: colors.gray[6] }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.white.DEFAULT,
                  border: `1px solid ${colors.gray[6]}`,
                  borderRadius: '6px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
                labelStyle={{ color: colors.gray[12], fontWeight: 500 }}
              />
              {config.yAxisKeys.map((key, index) => (
                <Scatter key={key} dataKey={key} name={config.legendNames?.[index] || key} fill={config.colors[index] || colors.primary[9]} />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        {renderCustomLegend('square')}
      </div>
    </div>
  );

  const renderRadarChart = () => {
    // For radar charts, check if there's a fullMark property to set domain
    const fullMark = typeof data[0]?.fullMark === 'number' ? data[0].fullMark : undefined;
    const radarDomain: [number, number] = fullMark ? [0, fullMark] : [0, domainMax];

    return (
      <div className='flex h-full flex-col justify-between'>
        <div className='flex flex-1 gap-4'>
          <div className='flex-1'>
            <ResponsiveContainer width='100%' height='100%'>
              <RadarChart cx='50%' cy='50%' outerRadius='80%' data={chartData}>
                <PolarGrid stroke={colors.gray.light} />
                <PolarAngleAxis dataKey={config.xAxisKey} tick={{ fill: colors.gray[11], fontSize: 12, spacing: 20 }} />
                <PolarRadiusAxis angle={90} domain={radarDomain} tick={{ fill: colors.gray[11], fontSize: 12 }} tickCount={5} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: colors.white.DEFAULT,
                    border: `1px solid ${colors.gray[6]}`,
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  }}
                  labelStyle={{ color: colors.gray[12], fontWeight: 500 }}
                />
                {config.yAxisKeys.map((key, index) => {
                  const translatedName = config.legendNames?.[index]?.[0] === 'value' ? 'Valor' : config.legendNames?.[index] || key;
                  return (
                    <Radar
                      key={key}
                      name={translatedName === 'value' ? 'Valor' : translatedName}
                      dataKey={key}
                      stroke={config.colors[index] || colors.primary[9]}
                      fill={config.colors[index] || colors.primary[9]}
                      fillOpacity={0.3}
                      strokeWidth={2}
                    />
                  );
                })}
              </RadarChart>
            </ResponsiveContainer>
          </div>
          {renderCustomLegend('line')}
        </div>
      </div>
    );
  };

  const renderComposedChart = () => (
    <div className='flex h-full flex-col justify-between'>
      <div className='flex flex-1 gap-4'>
        <div className='flex-1'>
          <ResponsiveContainer width='100%' height='100%'>
            <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid stroke={colors.gray.light} strokeDasharray='3 3' />
              <XAxis dataKey={config.xAxisKey} tick={{ fill: colors.gray[11], fontSize: 12 }} axisLine={{ stroke: colors.gray[6] }} />
              <YAxis tick={{ fill: colors.gray[11], fontSize: 12 }} axisLine={{ stroke: colors.gray[6] }} domain={[0, domainMax]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.white.DEFAULT,
                  border: `1px solid ${colors.gray[6]}`,
                  borderRadius: '6px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
                labelStyle={{ color: colors.gray[12], fontWeight: 500 }}
              />
              {config.yAxisKeys.map((key, index) => {
                // Alternate between Bar and Line for composed chart
                if (index % 2 === 0) {
                  return <Bar key={key} dataKey={key} name={config.legendNames?.[index] || key} fill={config.colors[index] || colors.success[9]} opacity={0.6} />;
                }
                return (
                  <Line
                    key={key}
                    type='monotone'
                    dataKey={key}
                    name={config.legendNames?.[index] || key}
                    stroke={config.colors[index] || colors.danger[9]}
                    strokeWidth={2}
                    dot={{ fill: config.colors[index] || colors.danger[9], r: 4 }}
                  />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {renderCustomLegend('square')}
      </div>
    </div>
  );

  const renderChart = () => {
    switch (chartType) {
      case 'line':
        return renderLineChart();
      case 'bar':
        return renderBarChart();
      case 'area':
        return renderAreaChart();
      case 'pie':
        return renderPieChart();
      case 'scatter':
        return renderScatterChart();
      case 'radar':
        return renderRadarChart();
      case 'composed':
        return renderComposedChart();
      default:
        return (
          <div className='flex h-64 items-center justify-center'>
            <Typography variant='text-md' color='secondary'>
              Unsupported chart type: {chartType}
            </Typography>
          </div>
        );
    }
  };

  return (
    <Card className='not-prose mt-4 w-full overflow-hidden border-gray-4 p-0 shadow-sm'>
      <Accordion type='single' collapsible className='w-full' defaultValue={undefined}>
        <AccordionItem value='chart' className='border-0'>
          <AccordionTrigger className='group flex w-full items-center justify-between gap-4 border-gray-4 border-b px-4 py-3 transition-all hover:bg-gray-2 focus:outline-none focus:ring-0 [&[data-state=open]]:bg-gray-2'>
            <div className='flex items-center gap-3'>
              <div className='flex size-8 shrink-0 items-center justify-center rounded-md bg-gray-3 text-gray-10 transition-colors group-hover:bg-gray-4'>
                <Icon name='chart-no-axes-column-increasing' size='sm' />
              </div>
              <div className='flex flex-col items-start gap-0.5'>
                <Typography variant='text-md-semibold' className='text-gray-12'>
                  {part.output.title || 'Visualização de dados'}
                </Typography>

                <Typography variant='text-sm' className='text-gray-9'>
                  Visualização de dados
                </Typography>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className='data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 p-0 focus:outline-none focus:ring-0 data-[state=closed]:animate-out data-[state=open]:animate-in'>
            <div className='h-80 w-full px-4'>{renderChart()}</div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};

export default ChartsDataTool;
